import {
  ArrowRightOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  EditOutlined,
  GlobalOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";
import { Button, Card, Divider, Result, Spin, Tag, Typography } from "antd";
import { ROUTES } from "Constants/AllRoutes";
import { Roles } from "Constants/roleEnum";
import { useUserInfo } from "GlobalStates/User";
import { useNavigate } from "react-router-dom";
import "./styles.css";
import { useQuery } from "@tanstack/react-query";
import { getHomeData } from "./apis";
import dayjs from "dayjs";

const { Title, Text } = Typography;

const roleContentByRole = {
  [Roles.ONEGRAD_HERO]: {
    title: "Working Professionals & Entrepreneurs",
    subtitle: "Mentor with real-world problem solving and industry insight.",
    focus: ["Career clarity", "Startup validation", "Leadership growth"],
  },
  [Roles.ONEGRAD_SENIOR]: {
    title: "Student Counselor",
    subtitle: "Guide students with structured preparation and confidence.",
    focus: ["Application strategy", "Interview readiness", "Skill planning"],
  },
  [Roles.ONEGRAD_COUNSELOR]: {
    title: "Professional Counselor",
    subtitle: "Deliver high-trust guidance with measurable outcomes.",
    focus: ["Goal tracking", "Behavioral coaching", "Placement readiness"],
  },
  [Roles.ONEGRAD_COLLEGE_REP]: {
    title: "College Representative",
    subtitle: "Showcase programs and connect with verified prospects.",
    focus: ["Program discovery", "Batch onboarding", "Partner reporting"],
  },
  default: {
    title: "Role-Aware Home",
    subtitle: "This page adapts to each role and can be extended easily.",
    focus: ["Personalized actions", "Role metrics", "Targeted guidance"],
  },
};

const quickActions = [
  {
    title: "My Calendar",
    description: "See upcoming sessions and manage daily schedule.",
    icon: <CalendarOutlined />,
    cta: "Open calendar",
    target: ROUTES.MY_CALENDAR,
  },
  {
    title: "Manage Appointments",
    description: "Cancel or propose reschedules from your slots view.",
    icon: <EditOutlined />,
    cta: "Edit slots",
    target: ROUTES.EDIT_SLOTS,
  },
  {
    title: "Publish Slots",
    description: "Create new availability for student bookings.",
    icon: <UsergroupAddOutlined />,
    cta: "Publish slots",
    target: ROUTES.PUBLISH_SLOTS,
  },
];

const impactCountries = {
  "United States": { name: "United States", flag: "🇺🇸" },
  "United Kingdom": { name: "United Kingdom", flag: "🇬🇧" },
  India: { name: "India", flag: "🇮🇳" },
  Canada: { name: "Canada", flag: "🇨🇦" },
  Singapore: { name: "Singapore", flag: "🇸🇬" },
};

export const Home = () => {
  const { user } = useUserInfo();
  const navigate = useNavigate();
  const roleContent =
    roleContentByRole[user?.role] || roleContentByRole.default;
  const displayName = user?.name?.split(" ")?.[0] || "there";
  const { data, isLoading, error } = useQuery({
    queryKey: ["homeMetrics", user?.name],
    queryFn: getHomeData,
  });

  if (isLoading) {
    return (
      <div className="h-full w-full flex justify-center items-center">
        <Spin size="large" />
      </div>
    );
  }
  if (error) {
    return <Result status="500" title="Error" subTitle="We'll be back soon" />;
  }

  const {
    bookedNext7Days,
    bookedUntilNow,
    totalRequestedSessions,
    upcomingSessionsWithNames,
    byCountry,
  } = data;

  return (
    <div className="home-page">
      <div className="home-hero">
        <div className="home-hero-content">
          <Title level={2} className="home-title">
            Welcome back, {displayName}
          </Title>
          <Text className="home-subtitle">
            Stay action-first with appointments, feedback, and your impact in
            one place.
          </Text>
          <div className="home-hero-actions">
            <Button
              type="primary"
              size="large"
              onClick={() => navigate(ROUTES.MY_CALENDAR)}
            >
              View appointments
            </Button>
            <Button
              size="large"
              className="home-secondary-button"
              onClick={() => navigate(ROUTES.PUBLISH_SLOTS)}
            >
              Publish slots
            </Button>
          </div>
        </div>
        <div className="home-stat-row">
          <div className="home-stat-block">
            <Text type="secondary">Upcoming sessions</Text>
            <Title level={3} className="home-stat-value">
              {bookedNext7Days}
            </Title>
            <Text className="home-stat-detail">Next 7 days</Text>
          </div>
          <div className="home-stat-divider" />
          <div className="home-stat-block">
            <Text type="secondary">Students helped</Text>
            <Title level={3} className="home-stat-value">
              {bookedUntilNow}
            </Title>
            <Text className="home-stat-detail">Lifetime impact</Text>
          </div>
        </div>
      </div>

      <div className="home-grid">
        <Card className="home-card" title="Quick actions" bordered={false}>
          <div className="home-action-grid">
            {quickActions.map((action) => (
              <div key={action.title} className="home-action">
                <div className="home-action-icon">{action.icon}</div>
                <div className="home-action-body">
                  <Text strong>{action.title}</Text>
                  <Text type="secondary">{action.description}</Text>
                  <Button
                    type="link"
                    onClick={() => navigate(action.target)}
                    className="home-action-link"
                  >
                    {action.cta} <ArrowRightOutlined />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card
          className="home-card"
          title="Manage appointments"
          bordered={false}
        >
          <div className="home-appointment">
            <div className="home-appointment-main">
              <CheckCircleOutlined className="home-appointment-icon" />
              <div>
                <Text strong>{"Pending confirmations "}</Text>
                <Text type="secondary">
                  {totalRequestedSessions} requests need approval
                </Text>
              </div>
            </div>
            <div className="home-appointment-actions">
              <Button
                type="primary"
                onClick={() => navigate(ROUTES.MY_CALENDAR)}
              >
                Open calendar
              </Button>
              <Button onClick={() => navigate(ROUTES.EDIT_SLOTS)}>
                Propose reschedule
              </Button>
              <Button
                className="home-secondary-button"
                onClick={() => navigate(ROUTES.EDIT_SLOTS)}
              >
                Cancel appointment
              </Button>
            </div>
          </div>
          <Divider />
          <div className="home-appointment-list">
            {upcomingSessionsWithNames.map(({ start, userName }) => (
              <div key={start} className="home-appointment-item">
                <Text strong>
                  {dayjs.utc(start).local().format("DD MMM YYYY, hh:mm A")}
                </Text>
                <Text type="secondary" className="mx-1">
                  {userName}
                </Text>
              </div>
            ))}
            <Button
              type="link"
              onClick={() => navigate(ROUTES.MY_CALENDAR)}
              className="home-action-link"
            >
              View all appointments <ArrowRightOutlined />
            </Button>
          </div>
        </Card>

        <Card className="home-card" title="Impact by country" bordered={false}>
          <div className="home-impact-header">
            <GlobalOutlined className="home-impact-icon" />
            <Text type="secondary">Students helped across regions</Text>
          </div>
          <div className="home-impact-grid">
            {byCountry.map(({ country, bookedSlots }) => (
              <div key={country} className="home-impact-item">
                <span className="home-impact-flag">
                  {impactCountries[country].flag}
                </span>
                <div>
                  <Text strong className="mx-1">
                    {country}
                  </Text>
                  <Text type="secondary">{bookedSlots} students</Text>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="home-card" title="Role focus" bordered={false}>
          <Tag color="green" className="home-role-pill">
            {roleContent.title}
          </Tag>
          <Text className="home-role-subtitle">{roleContent.subtitle}</Text>
          <div className="home-role-focus">
            {roleContent.focus.map((item) => (
              <div key={item} className="home-role-focus-item">
                <CheckCircleOutlined />
                <Text>{item}</Text>
              </div>
            ))}
          </div>
          <Divider />
        </Card>
      </div>
    </div>
  );
};
